import { Download, Loader2 } from "lucide-react";
import { useBlobUrl } from "@/lib/protectedFile";

/**
 * Shows a file loaded by `useBlobUrl`, according to what the blob actually is and never what its name
 * says. A PDF goes in an unsandboxed frame and an image in an `<img>`; plain text goes in a frame with
 * `sandbox=""`; every other type — `text/html`, `image/svg+xml`, an unknown or empty type — is only
 * offered as a download. A blob URL is same-origin with the portal, so an HTML or SVG file framed from
 * one would run as script with the staff member's session; see `lib/safeBlob.js`. A PDF cannot be
 * sandboxed (Chrome's viewer will not render inside a sandboxed frame), so for a PDF the allowlist
 * is the whole guard.
 *
 * Every preview of a file served by the API renders through this; `noBlobUrlOutsideSafeBlob.test.js`
 * fails if one builds its own frame from an object URL.
 *
 * @param {object} props
 * @param {{status: string, url: string|null, kind: string|null, error: string|null}} props.state `useBlobUrl` result.
 * @param {string} props.name file name, used as the frame title, the image alt text and the download name.
 * @param {string} [props.downloadName] name to save the file under, when it differs from `name`.
 * @param {string} [props.frameClassName] classes for the PDF and text frame.
 * @param {string} [props.imageClassName] classes for the `<img>`. A dialog with no set height must cap the
 *   image itself (`max-h-[72vh]`), because `max-h-full` of an auto-height parent caps nothing.
 * @param {string} [props.imageWrapperClassName] classes for the box around the `<img>`.
 * @param {string} [props.frameTestId] test id for the PDF and text frame.
 * @param {string} [props.testIdPrefix] prefix of the loading, error and download test ids.
 */
export function BlobPreview({
  state,
  name,
  downloadName,
  frameClassName = "w-full h-full",
  imageClassName = "max-w-full max-h-full object-contain",
  imageWrapperClassName = "h-full overflow-auto flex items-center justify-center p-4",
  frameTestId,
  testIdPrefix = "blob-preview",
}) {
  if (state.status === "error") {
    return (
      <div
        className="h-full min-h-[8rem] flex items-center justify-center p-6 text-sm text-red-700"
        role="alert"
        data-testid={`${testIdPrefix}-error`}
      >
        {state.error}
      </div>
    );
  }
  if (state.status !== "ready") {
    return (
      <div className="h-full min-h-[8rem] flex items-center justify-center" data-testid={`${testIdPrefix}-loading`}>
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  const { url, kind } = state;
  if (kind === "download") {
    return (
      <div
        className="h-full min-h-[8rem] flex flex-col items-center justify-center gap-3 p-6 text-sm text-muted-foreground"
        data-testid={`${testIdPrefix}-download-only`}
      >
        <div>No inline preview for this file type.</div>
        <a
          href={url}
          download={downloadName || name}
          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
          data-testid={`${testIdPrefix}-download`}
        >
          <Download className="w-3.5 h-3.5" />
          Download {name}
        </a>
      </div>
    );
  }
  if (kind === "image") {
    return (
      <div className={imageWrapperClassName}>
        <img src={url} alt={name} className={imageClassName} />
      </div>
    );
  }
  return (
    <iframe
      title={name}
      src={kind === "pdf" ? `${url}#view=FitH&toolbar=1` : url}
      sandbox={kind === "pdf" ? undefined : ""}
      className={frameClassName}
      style={{ border: 0 }}
      data-testid={frameTestId || `${testIdPrefix}-iframe`}
    />
  );
}

/**
 * Loads a file through the API client and previews it: `useBlobUrl` plus `BlobPreview`.
 *
 * @param {object} props
 * @param {string} props.loadKey identifies the file; a new key loads a new one.
 * @param {(signal: AbortSignal) => Promise<Blob>} props.load fetches the blob through `api.js`.
 * @param {string} props.name see `BlobPreview`.
 */
export default function BlobFilePreview({ loadKey, load, ...rest }) {
  return <BlobPreview state={useBlobUrl(loadKey, load)} {...rest} />;
}
